import type { CSSProperties, MouseEvent, ReactNode } from 'react'

import { cn } from '@/ui/lib/utils'

export interface TopNavItem {
  label: string
  href: string
  active: boolean
  /**
   * Click-interception handler — a router-aware caller passes
   * `(e) => { e.preventDefault(); navigate(href) }` here to keep navigation
   * client-side (a plain `<a href>` would otherwise trigger a full page
   * reload); this component never imports `react-router` itself.
   */
  onClick?: (event: MouseEvent) => void
}

interface TopNavProps {
  items: TopNavItem[]
  /**
   * An optional trailing action, rendered in the right-aligned trailing
   * group immediately before the last item (Settings) — e.g. spec
   * 030-feedback-trigger-button REV-1's top-bar "Give feedback" button.
   * Purely presentational: `TopNav` renders whatever `ReactNode` it's given
   * and never imports app-layer concerns (`useFeedback`, `react-router`,
   * etc.) itself — the caller in `src/app/` supplies an already-wired node.
   */
  trailingAction?: ReactNode
  /** QMC adaptation (spec 004): the bar's wordmark. Defaults to echo's own. */
  wordmark?: string
  /** QMC adaptation (spec 004): false keeps every item in the left group (echo puts the last one, Settings, at the right). */
  lastRight?: boolean
  /** QMC adaptation (spec 004): the bar's `--h`; echo's house hue 28 by default, QMC passes 193. */
  hue?: number
}

/**
 * A single top-nav link (spec 014 task 2a): Archivo Narrow uppercase tracked
 * label, sized to match the wordmark (11px/.22em). Active state is never
 * colour-alone (a11y) — it also gets a heavier weight and a real 2px
 * `--accent-line` underline rule rendered as its own element, distinct from
 * inactive links. Text colour reads the Inset scheme's `--bar-tx` (never a
 * hardcoded ink/paper token) — active is the full `--bar-tx`, inactive a 52%
 * `color-mix`, and hover moves an inactive link to the full colour only
 * (never a background/fill).
 */
function TopNavLink({ item }: { item: TopNavItem }) {
  return (
    <a
      href={item.href}
      onClick={item.onClick}
      aria-current={item.active ? 'page' : undefined}
      className={cn(
        'relative flex h-full items-center px-[14px] font-sans text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors',
        item.active
          ? cn('font-bold', 'text-[var(--bar-tx,var(--ink))]')
          : cn(
              'text-[color-mix(in_oklab,var(--bar-tx,var(--ink))_52%,transparent)]',
              'hover:text-[var(--bar-tx,var(--ink))]',
            ),
      )}
    >
      {item.label}
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-x-0 bottom-0 h-[2px]',
          item.active ? 'bg-[var(--accent-line)]' : 'bg-transparent',
        )}
      />
    </a>
  )
}

/**
 * Station Kit v3's L1 station bar (spec 014 task 2a, spec.md Proposed Design
 * §1): a 46px band, `align-items: stretch`, one 24px gutter (`padding: 0
 * 24px`), `gap: 10px`. It carries the EMBER house hue (`--h: 28`, the
 * default) since no single agent/project owns global chrome. Settings sits
 * at the top right (SET-1); the remaining items (Agents/Projects) stay left,
 * after the wordmark.
 *
 * The wordmark now matches the nav items' own size (11px/.22em) and carries a
 * `border-bottom: 2px solid transparent` purely so its baseline lines up with
 * an active tab's real underline — a 1px×14px hairline (reading `--bar-rule`)
 * separates it from the nav items in place of the old wide gap.
 *
 * Reads the Inset nav-scheme vars (`--bar-bg`/`--bar-tx`/`--bar-rule`, spec
 * 014 task 0.2) with `var(--x, var(--paper|--ink))`-style fallbacks so the
 * bar still paints correctly even if a consumer never sets them.
 *
 * Presentational only (spec 009-design-sync T1.7) — takes a flat list of
 * `{ label, href, active, onClick? }` entries and renders plain `<a>` tags,
 * never `react-router`'s `NavLink`. `App.tsx` keeps the thin wrapper that
 * computes `active` from the current route and wires up client-side
 * navigation.
 */
export function TopNav({ items, trailingAction, wordmark = 'QUANT MISSION CONTROL', lastRight = true, hue = 28 }: TopNavProps) {
  const [first, ...rest] = items
  const last = lastRight ? rest.pop() : undefined

  return (
    <nav
      aria-label="Main"
      style={hue === undefined ? undefined : ({ '--h': hue } as CSSProperties)}
      className="flex h-[46px] shrink-0 items-stretch justify-between gap-[10px] border-b border-[var(--bar-rule,var(--ink-20))] bg-[var(--bar-bg,var(--paper))] px-6"
    >
      <div className="flex items-stretch gap-[10px]">
        <span className="flex h-full items-center border-b-2 border-transparent font-sans text-[11px] font-bold tracking-[0.22em] text-[var(--bar-tx,var(--ink))] uppercase">
          {wordmark}
        </span>
        <span aria-hidden="true" className="h-[14px] w-px self-center bg-[var(--bar-rule,var(--ink-20))]" />
        <div className="flex items-stretch">
          {first && <TopNavLink item={first} />}
          {rest.map((item) => (
            <TopNavLink key={item.href} item={item} />
          ))}
        </div>
      </div>
      <div className="flex items-stretch">
        {trailingAction}
        {last && <TopNavLink item={last} />}
      </div>
    </nav>
  )
}
